/* oxlint-disable tailwindcss/no-unknown-classes -- Styles are colocated in home-new.css. */
import { useEffect, useState } from "react"

import depotSymbolSrc from "@/assets/home-new/ci/depot-symbol.svg"
import providerWordmarkDarkSrc from "@/assets/home-new/ci/provider-wordmark-dark.svg"
import providerWordmarkSrc from "@/assets/home-new/ci/provider-wordmark.svg"
import { FloatingShell } from "@/components/FloatingShell"
import { GateStatusGlyph } from "@/components/trails/GateStatusGlyph"
import { PluginGateLogo } from "@/components/trails/PluginGateLogo"
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion"
import { TrailChevronIcon } from "@/routes/_app/$forge/$project/$repo/trails/$number/{-$slug}/changes/-components/TrailChevronIcon"

import { useRevealOnce } from "../useRevealOnce"
import { BuildkiteLogo, DaggerLogo, DepotLogo } from "./ciPartnerLogos"
import { illustrationCardClassName } from "./illustrationKit"

// Ordered by how long each check takes, so they finish shortest first.
const checkSteps = [
  { name: "Packages", failed: false, duration: "21s" },
  { name: "Website", failed: false, duration: "32s" },
  { name: "API", failed: false, duration: "1m 47s" },
  { name: "Frontend", failed: true, duration: "3m 24s" },
] as const

// Display order, as the gate lists them: the failure first.
const listedSteps = [3, 2, 0, 1] as const

const STEP_INTERVAL_MS = 420

/**
 * A partner CI suite on a Trail, built from the app's gate parts: Depot's
 * checks start running as the illustration scrolls into view and resolve one
 * by one, the frontend build failing last. Reduced motion shows the result.
 */
export function CiChecksIllustration() {
  const [revealed, ref] = useRevealOnce()
  const prefersReducedMotion = usePrefersReducedMotion()
  const [resolvedCount, setResolvedCount] = useState(0)
  const finished = revealed && prefersReducedMotion ? checkSteps.length : resolvedCount

  // Resolves the next check every STEP_INTERVAL_MS once revealed.
  useEffect(() => {
    if (!revealed || finished >= checkSteps.length) return
    const timer = window.setTimeout(() => setResolvedCount((count) => count + 1), STEP_INTERVAL_MS)
    return () => window.clearTimeout(timer)
  }, [revealed, finished])

  const done = finished >= checkSteps.length
  const failures = checkSteps.filter((step) => step.failed).length

  return (
    <div ref={ref} aria-hidden="true" inert className="flex w-full min-w-0 font-sans">
      <FloatingShell
        className="w-full rounded-xl"
        innerClassName="overflow-clip rounded-[calc(var(--radius-xl)-1px)] p-1"
      >
        <div className="flex min-h-10 items-center gap-3 rounded-lg py-1 pr-2 pl-3">
          <GateStatusGlyph tone={done ? "failed" : "pending"} active={!done} />
          <span className="flex min-w-0 flex-1 items-center gap-3">
            <span className="flex shrink-0 items-center gap-2">
              <PluginGateLogo src={depotSymbolSrc} />
              <span className="text-sm leading-4 text-default">Depot CI</span>
            </span>
            <span
              className={`min-w-0 truncate text-xs leading-4 ${done ? "text-amber-700 dark:text-amber-400" : "text-muted"}`}
            >
              {done ? `${failures} of ${checkSteps.length} failed` : "Running"}
            </span>
          </span>
          <span className="flex size-7 shrink-0 items-center justify-center">
            <TrailChevronIcon className="h-[3.8px] w-[6.6px] shrink-0 -scale-y-100 text-[#79716B]" />
          </span>
        </div>
        <ul className="m-0 list-none pr-2 pb-2 pl-11">
          {listedSteps.map((index) => {
            const step = checkSteps[index]
            const resolved = index < finished
            return (
              <li key={step.name} className="flex min-h-8 items-center gap-3 py-1">
                <GateStatusGlyph
                  tone={resolved ? (step.failed ? "failed" : "success") : "pending"}
                  active={revealed && !resolved}
                />
                <span className="min-w-0 truncate text-sm leading-4 text-default">{step.name}</span>
                <span
                  className={`shrink-0 text-xs leading-4 ${resolved && step.failed ? "text-amber-700 dark:text-amber-400" : "text-muted"}`}
                >
                  {resolved ? (step.failed ? "Failed" : "Passed") : revealed ? "Running" : "Queued"}
                </span>
                <span
                  className={`ml-auto shrink-0 text-xs leading-4 text-muted transition-opacity duration-300 ${resolved ? "opacity-100" : "opacity-0"}`}
                >
                  {step.duration}
                </span>
              </li>
            )
          })}
        </ul>
      </FloatingShell>
    </div>
  )
}

const partnerCardClassName = `@container flex min-w-0 items-center justify-center rounded-xl text-default ${illustrationCardClassName}`

/**
 * The CI partners as two columns of logo cards, with the next ones peeking in
 * above and below under a fade. The cards rise in the first time it scrolls
 * into view (styles: `.home-new-ci-partners`).
 */
export function CiPartnersIllustration() {
  const [revealed, ref] = useRevealOnce()

  return (
    <div
      ref={ref}
      data-revealed={revealed ? "true" : "false"}
      aria-hidden="true"
      inert
      className="home-new-ci-partners grid h-[260px] w-[calc(100%+32px)] max-w-[522px] min-w-0 grid-cols-2 content-center gap-2.5 self-center px-4"
    >
      <div className={`${partnerCardClassName} h-5 rounded-t-none`} />
      <div className={`${partnerCardClassName} h-5 rounded-t-none`} />
      <div data-ci-partner className={`${partnerCardClassName} h-24 px-4 sm:px-6`}>
        {/* Nudged 4px down: the wordmark sits high in its box. */}
        <span data-ci-logo className="mt-1 -mb-1">
          <BuildkiteLogo className="h-full w-auto" />
        </span>
      </div>
      <div data-ci-partner className={`${partnerCardClassName} h-24 px-4 sm:px-6`}>
        <span data-ci-logo="dagger">
          <DaggerLogo className="h-full w-auto" />
        </span>
      </div>
      <div data-ci-partner className={`${partnerCardClassName} h-24 px-4 sm:px-6`}>
        <span data-ci-logo="depot">
          <DepotLogo className="h-full w-auto" />
        </span>
      </div>
      <div data-ci-partner className={`${partnerCardClassName} h-24 px-4 sm:px-6`}>
        <span data-ci-logo>
          <img
            src={providerWordmarkSrc}
            alt=""
            width={158.3}
            height={40}
            className="block h-full w-auto dark:hidden"
          />
          <img
            src={providerWordmarkDarkSrc}
            alt=""
            width={158.3}
            height={40}
            className="hidden h-full w-auto dark:block"
          />
        </span>
      </div>
      <div className={`${partnerCardClassName} h-5 rounded-b-none`} />
      <div className={`${partnerCardClassName} h-5 rounded-b-none`} />
    </div>
  )
}
